import axios from "axios";
import { toast } from "react-toastify";
import { clearUserSession, getAuthToken } from "./auth";

function getBaseURL() {
  return process.env.REACT_APP_API_URL;
}

export function getChartsBaseUrl() {
  return process.env.REACT_APP_CHARTS_BASE_URL;
}

const apiInstance = axios.create({
    baseURL: ''
});

let sessionExpiredShown = false;

export function isNetworkError(error: any): boolean {
    return !navigator.onLine && !error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error');
}

apiInstance.interceptors.response.use(
    response => response,
    error => {
        // Skip toast for network errors — App.tsx shows full NoInternet page when offline
        if (isNetworkError(error)) {
            return Promise.reject(error);
        }
        if (error.response && error.response.status === 401) {
            clearUserSession();
            const loginPath = '/login';
            if (window.location.pathname !== loginPath) {
                if (!sessionExpiredShown) {
                    sessionExpiredShown = true;
                    toast.error("Your session has expired. Please login again.");
                    setTimeout(() => {
                        window.location.href = loginPath;
                    }, 2000);
                }
            }
        }
        return Promise.reject(error);
    }
);


function getHeaders(headers = {}): any {
    const authToken = getAuthToken();
    if (authToken) {
        return {
          'Accept': 'application/json',
            Authorization: `bearer ${authToken}`,
            ...headers
        }
    } else {
        return headers;
    }
}

function getQueryString(payload: any) {
    const keys = Object.keys(payload);
    const queries: any[] = [];
    keys.forEach(key => {
        const value = payload[key];
        if (typeof value !== undefined) {
            queries.push(`${key}=${value}`);
        }
    });
    return queries.join('&');
}

function toRailsQuery(obj: any, prefix = ''): string {
  if (obj === null || obj === undefined) return '';

  const queryParts: string[] = [];

  Object.keys(obj).forEach(key => {
    const value = obj[key];
    const paramKey = prefix ? `${prefix}[${key}]` : key;

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (typeof item === 'object' && item !== null) {
          queryParts.push(toRailsQuery(item, `${paramKey}[${index}]`));
        } else {
          queryParts.push(`${encodeURIComponent(paramKey)}[]=${encodeURIComponent(item)}`);
        }
      });
    } else if (typeof value === 'object' && value !== null) {
      queryParts.push(toRailsQuery(value, paramKey));
    } else {
      queryParts.push(`${encodeURIComponent(paramKey)}=${encodeURIComponent(value)}`);
    }
  });

  return queryParts.join('&');
}


export function get(url: string, payload?: any, config?: any, others?: any) {
    if (Object.keys(payload || {}).length > 0) {
        url = `${url}?${toRailsQuery(payload)}`
    }
    const _url = url.indexOf('http') === 0 ? url : `${getBaseURL()}${url}`;
    return apiInstance.get(_url, { headers: getHeaders(config), ...others });
}

export function post(url: string, payload: any, config?: any, sendHeaders = true, others?: any) {
    const _url = url.indexOf('http') === 0 ? url : `${getBaseURL()}${url}`;
    return apiInstance.post(_url, payload, sendHeaders ? { headers: getHeaders(config), ...others } : null);
}

export function put(url: string, payload?: any, config?: any, sendHeaders = true) {
    const _url = url.indexOf('http') === 0 ? url : `${getBaseURL()}${url}`;
    return apiInstance.put(_url, payload, sendHeaders ? { headers: getHeaders(config) } : undefined);
}

export function del(url: string, payload?: any, config?: any, sendHeaders = true) {
    const _url = url.indexOf('http') === 0 ? url : `${getBaseURL()}${url}`;
    return apiInstance.delete(_url, { headers: getHeaders(config) });
}
